import {
  ClientResponse,
  ConversationEventType,
  ConversationParticipant,
  SendFeedbackParams,
} from '@stripe-internal/conversations-client';
import {ConversationsApiSendMessageEndpointRequest} from '../generated/types';
import {ApiMethods, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';
import {
  buildDeltaResponseState,
  emitAddedMessages,
  emitDeltaResponse,
} from './SendResponseHandling';

type DeleteConversationResponse =
  | {type: 'success'; data: undefined}
  | {type: 'error'; error: string; code: number};

type SendFeedbackHandlerDeps = {
  sendMessage: (
    conversationId: string,
    payload: ConversationsApiSendMessageEndpointRequest,
    options: {allowNoContent: true},
  ) => ReturnType<ApiMethods['sendMessage']>;
  deleteConversation?: (
    conversationId: string,
  ) => Promise<DeleteConversationResponse>;
  emitConversationEvent: (event: ConversationEventType) => void;
  getParticipant: () => ConversationParticipant | undefined;
  attachmentDownloadPath?: string;
  reportWarning?: ReportWarning;
};

export class SendFeedbackHandler {
  private readonly deps: SendFeedbackHandlerDeps;

  constructor(deps: SendFeedbackHandlerDeps) {
    this.deps = deps;
  }

  call = async (params: SendFeedbackParams): Promise<ClientResponse> => {
    const participant = this.deps.getParticipant();
    if (!participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }

    const response = await this.deps.sendMessage(
      params.conversationId,
      SendFeedbackHandler.buildPayload({params, participant}),
      {allowNoContent: true},
    );
    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    if (!response.data) {
      return {code: 200};
    }

    const {
      context,
      ended,
      messages: newMessages,
    } = buildDeltaResponseState({
      conversationId: params.conversationId,
      apiMessages: response.data.messages,
      participantsById: {[participant.id]: participant},
      user: participant,
      reportWarning: this.deps.reportWarning,
      attachmentDownloadPath: this.deps.attachmentDownloadPath,
    });
    await emitDeltaResponse({
      emitConversationEvent: this.deps.emitConversationEvent,
      conversationId: params.conversationId,
      context,
      ended,
      deleteConversation: this.deps.deleteConversation,
      typingEndedParticipantId: participant.id,
      reportWarning: this.deps.reportWarning,
      emitMessages: () =>
        emitAddedMessages(
          this.deps.emitConversationEvent,
          params.conversationId,
          newMessages,
        ),
    });

    return {code: 200};
  };

  private static buildPayload({
    params,
    participant,
  }: {
    params: SendFeedbackParams;
    participant: ConversationParticipant;
  }): ConversationsApiSendMessageEndpointRequest {
    return {
      id: ApiMethods.generateMessageId(),
      participant_id: participant.id,
      participant_type: FrameworkAdapter.participantTypeFromRole(
        participant.role,
      ),
      parts: [
        {
          id: 1,
          type: 'thumbs_feedback',
          content: {
            text: params.feedback,
            feedback_value: params.feedback,
            message_id: params.responseId,
          },
        },
      ],
    };
  }
}
